import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { StatusTone } from "../lib/overview-ops";
import { cmsStrings } from "../terminology";

type Props = {
  title: string;
  description?: string;
  actionLabel?: string;
  actionTo?: string;
  action?: ReactNode;
  headingLevel?: "h2" | "h3";
};

export function SectionHeader({
  title,
  description,
  actionLabel,
  actionTo,
  action,
  headingLevel = "h2",
}: Props) {
  const Heading = headingLevel;
  return (
    <div className="section-header">
      <div className="section-header__text">
        <Heading className="section-header__title">{title}</Heading>
        {description && <p className="section-header__description">{description}</p>}
      </div>
      {action
        ? action
        : actionLabel && actionTo
          ? (
              <Link to={actionTo} className="section-header__action">
                {actionLabel}
              </Link>
            )
          : null}
    </div>
  );
}

type EmptyProps = {
  title: string;
  description?: string;
  actionLabel?: string;
  actionTo?: string;
};

export function OpsEmptyState({ title, description, actionLabel, actionTo }: EmptyProps) {
  return (
    <div className="ops-empty" role="status" data-vt-empty-state>
      <p className="ops-empty__title">{title}</p>
      {description && <p className="ops-empty__description">{description}</p>}
      {actionLabel && actionTo && (
        <Link to={actionTo} className="btn btn--ghost btn--sm">
          {actionLabel}
        </Link>
      )}
    </div>
  );
}

/** First-paint placeholder for Mercury inventory lists. */
export function InventorySkeleton() {
  return (
    <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
      <div className="inventory-skeleton__row" style={{ width: "92%" }} />
      <div className="inventory-skeleton__row" style={{ width: "78%" }} />
      <div className="inventory-skeleton__row" style={{ width: "85%" }} />
      <div className="inventory-skeleton__row" style={{ width: "70%" }} />
      <div className="inventory-skeleton__row" style={{ width: "88%" }} />
    </div>
  );
}

type HealthRowProps = {
  label: string;
  value: string;
  tone: StatusTone;
  hint?: string;
  to?: string;
  loading?: boolean;
  unavailable?: boolean;
};

const TONE_BADGE: Record<StatusTone, string> = {
  success: "state-badge--published",
  warning: "state-badge--paused",
  danger: "state-badge--emergency",
  info: "state-badge--scheduled",
  neutral: "state-badge--archived",
};

export function HealthRow({
  label,
  value,
  tone,
  hint,
  to,
  loading,
  unavailable,
}: HealthRowProps) {
  const body = (
    <>
      <span className="health-row__label">{label}</span>
      <span className="health-row__value">
        {unavailable ? (
          <span className={`state-badge ${TONE_BADGE.neutral}`}>
            {cmsStrings.shell.unavailable}
          </span>
        ) : (
          <span className={`state-badge ${TONE_BADGE[tone]}`}>
            {loading ? cmsStrings.shell.loading : value}
          </span>
        )}
      </span>
      {hint && !unavailable && <span className="health-row__hint">{hint}</span>}
    </>
  );

  if (to && !unavailable) {
    return (
      <Link to={to} className="health-row health-row--link">
        {body}
      </Link>
    );
  }

  return <div className="health-row">{body}</div>;
}

type AlertItemProps = {
  title: string;
  severityLabel: string;
  tone: StatusTone;
  firedAt: string;
  status?: string;
};

export function AlertListItem({
  title,
  severityLabel,
  tone,
  firedAt,
  status,
}: AlertItemProps) {
  return (
    <li className="alert-list-item">
      <div className="alert-list-item__main">
        <span className={`state-badge ${TONE_BADGE[tone]}`}>{severityLabel}</span>
        <span className="alert-list-item__title">{title}</span>
      </div>
      <div className="alert-list-item__meta">
        {status && <span>{status}</span>}
        <bdi dir="ltr" className="ltr-isolate latin-digits">
          {firedAt}
        </bdi>
      </div>
    </li>
  );
}
